import { type ZodError, z } from 'zod'

export const ErrorResponseSchema = z.object({
  message: z.string(),
  customCode: z.string(),
})

/** Keeps a successful Result's envelope and applies the data schema. */
export function SuccessSchema<Output, Input>(schema: z.ZodType<Output, Input>) {
  return z.object({ ok: z.literal(true), data: schema })
}

/** Accepts a successful Result and returns its transformed data. */
export function DataSchema<Output, Input>(schema: z.ZodType<Output, Input>) {
  return SuccessSchema(schema).transform(({ data }) => data)
}

/** Keeps a failed Result's envelope and strips internal error diagnostics. */
export function FailureSchema<const CustomCode extends string>(customCode: CustomCode) {
  const schema = z.object({
    ok: z.literal(false),
    error: ErrorResponseSchema.extend({
      customCode: z.literal(customCode),
    }),
  })

  return Object.assign(schema, {
    message: (text: string) => schema.transform(withErrorMessage(text)).pipe(schema),
  })
}

/** Accepts a failed Result and returns its HTTP error body. */
export function ErrorSchema<const CustomCode extends string>(customCode: CustomCode) {
  const failureSchema = FailureSchema(customCode)

  return failureSchema.transform(({ error }) => error).pipe(failureSchema.shape.error)
}

export { failure, type Result, success, toError } from './traced-results.ts'
export const withErrorMessage =
  (message: string) =>
  <Failure extends { ok: false; error: { message: string } }>(result: Failure) => ({
    ...result,
    error: { ...result.error, message },
  })

export function zodErrorToString(error: ZodError): string {
  return error.issues
    .map(issue => {
      const path = issue.path.join('.') || '<root>'
      return `${path}: ${issue.message}`
    })
    .join('; ')
}
